import * as fs from 'fs';
import * as path from 'path';
import type { MatrixVariantRow } from './survey-inventory-extract';

export const LAYOUT_AUDIT_FILENAME = 'layout-audit.md';

const AUDIT_HEADER = `# Layout audit (HOOK 3 — blocking before preview)

> Fill every row after code audit vs \`implementation_plan.md\` §3c. **Broken must be 0** before \`npx cfm-sdk verify-layout\` passes.
> Template: [\`reverification-hooks.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/reverification-hooks.md) §HOOK 3 audit report.

## Verification summary

| Status | Count | Meaning |
| Broken | 0 | Must be 0 before preview |
| Needs improvement | 0 | P2 polish — non-blocking |
| OK | 0 | Code matches §3c variant |

## Matrix format (per §3c row)

| questionId | type | displayStyle | labelIndexMap | nonNaM | naTrailing | carouselParity | uxShell | Verdict | Doc anchor |
|------------|------|--------------|---------------|--------|------------|----------------|---------|---------|------------|
`;

function auditRowCells(row: MatrixVariantRow): string {
  return `| ${row.questionId} | ${row.type} | ${row.displayStyle} |  |  |  |  |  |  | layout-cookbook |`;
}

export function buildLayoutAuditContent(variants: MatrixVariantRow[]): string {
  if (!variants.length) {
    return `${AUDIT_HEADER}| (no matrix questions in fetch) | | | | | | | | | | |\n`;
  }
  const rows = variants.map(auditRowCells).join('\n');
  return `${AUDIT_HEADER}${rows}\n`;
}

export function writeLayoutAuditFile(projectRoot: string, variants: MatrixVariantRow[]): string {
  const auditPath = path.join(projectRoot, LAYOUT_AUDIT_FILENAME);
  fs.writeFileSync(auditPath, buildLayoutAuditContent(variants), 'utf8');
  return auditPath;
}

export interface LayoutAuditParseResult {
  ok: boolean;
  errors: string[];
  brokenCount: number;
  incompleteCount: number;
}

const ALLOWED_VERDICTS = new Set(['ok', 'needs improvement']);

export function parseLayoutAuditFile(projectRoot: string): LayoutAuditParseResult {
  const auditPath = path.join(projectRoot, LAYOUT_AUDIT_FILENAME);
  const errors: string[] = [];

  if (!fs.existsSync(auditPath)) {
    return {
      ok: false,
      errors: [
        `${LAYOUT_AUDIT_FILENAME} missing — run npx cfm-sdk sync-from-fetch to generate it, then complete HOOK 3 audit rows`,
      ],
      brokenCount: 0,
      incompleteCount: 0,
    };
  }

  const content = fs.readFileSync(auditPath, 'utf8');
  const tableSection = content.split('## Matrix format')[1] ?? '';
  const lines = tableSection.split('\n').filter((l) => l.trim().startsWith('|'));

  let brokenCount = 0;
  let incompleteCount = 0;
  let dataRows = 0;

  for (const line of lines) {
    if (line.includes('questionId')) continue;
    if (/^[\s|:-]+$/.test(line)) continue;
    if (line.includes('(no matrix questions in fetch)')) continue;

    const cells = line.split('|').map((c) => c.trim());
    const dataCells = cells.slice(1, cells.at(-1) === '' ? -1 : undefined);
    if (dataCells.length < 2) continue;

    dataRows += 1;
    const questionId = dataCells[0];
    const verdict = (dataCells[8] ?? '').trim().toLowerCase();

    if (!verdict) {
      incompleteCount += 1;
      errors.push(`${LAYOUT_AUDIT_FILENAME}: ${questionId} — Verdict column empty (set OK or Needs improvement)`);
      continue;
    }
    if (verdict === 'broken') {
      brokenCount += 1;
      errors.push(`${LAYOUT_AUDIT_FILENAME}: ${questionId} — Verdict is Broken (fix before preview)`);
      continue;
    }
    if (!ALLOWED_VERDICTS.has(verdict)) {
      incompleteCount += 1;
      errors.push(
        `${LAYOUT_AUDIT_FILENAME}: ${questionId} — Verdict must be OK or Needs improvement (got "${verdict}")`,
      );
    }
  }

  if (dataRows === 0 && !content.includes('(no matrix questions in fetch)')) {
    errors.push(`${LAYOUT_AUDIT_FILENAME}: no §3c audit rows found — fill Matrix format table`);
  }

  return {
    ok: errors.length === 0,
    errors,
    brokenCount,
    incompleteCount,
  };
}
